import {
    type AccountInfo,
    BrowserAuthError,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { Button, Center, Flex, Text, useMantineTheme } from "@mantine/core";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { scopesNeeded } from "src/utils/authConfig";

// Get auth config from environment - required, no fallbacks
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID;
const tenantId = import.meta.env.VITE_AUTH_TENANT_ID;

if (!clientId || !tenantId) {
    throw new Error(
        "Copilot authentication configuration missing. Please ensure VITE_AUTH_CLIENT_ID and VITE_AUTH_TENANT_ID environment variables are set.",
    );
}

// Copilot-specific MSAL configuration for iframe usage
const msalConfig = {
    auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: "localStorage" as const,
        storeAuthStateInCookie: true,
    },
    system: {
        allowRedirectInIframe: false,
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
        asyncPopups: true,
    },
};

// Initialize MSAL instance for copilot
const msalInstance = new PublicClientApplication(msalConfig);

interface CopilotAuthProviderProps {
    children: ReactNode;
    authMode?: "sso" | "popup" | "inherit";
    loginHint?: string;
    sid?: string;
}

export function CopilotAuthProvider({
    children,
    authMode = "popup",
    loginHint,
    sid,
}: CopilotAuthProviderProps) {
    const theme = useMantineTheme();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [_account, setAccount] = useState<AccountInfo | null>(null);
    // Tracks whether msalInstance.initialize() has completed.
    // handleMessage must not call loginPopup before initialization finishes —
    // doing so throws BrowserAuthError: uninitialized_public_client_application.
    const msalInitialized = useRef(false);
    // Captures a loginHint received via postMessage so the popup fallback can
    // pre-select the right account when ssoSilent fails (e.g. due to enterprise
    // BSSO extensions interfering with MSAL's hidden-iframe monitor).
    const [messageLoginHint, setMessageLoginHint] = useState<string | undefined>(loginHint);

    useEffect(() => {
        const initAuth = async () => {
            try {
                await msalInstance.initialize();
                msalInitialized.current = true;

                // Check if we already have an account
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    const account = accounts[0];
                    msalInstance.setActiveAccount(account);
                    setAccount(account);

                    // Validate the cached token is still usable
                    try {
                        await msalInstance.acquireTokenSilent({
                            scopes: scopesNeeded,
                            account: account,
                            forceRefresh: false,
                        });
                        // Token is valid, user is authenticated
                        setIsAuthenticated(true);
                        setIsLoading(false);
                        return;
                    } catch (tokenError) {
                        if (tokenError instanceof InteractionRequiredAuthError) {
                            // Token expired and needs re-authentication
                            console.info(
                                "[Copilot] Cached token expired, user needs to re-authenticate",
                            );
                            // Don't set error yet - show login button instead
                        } else {
                            // Non-critical error (e.g., network), try to proceed
                            console.warn(
                                "[Copilot] Token validation failed, proceeding:",
                                tokenError,
                            );
                            setIsAuthenticated(true);
                            setIsLoading(false);
                            return;
                        }
                    }
                }

                // Try SSO silent authentication
                if (authMode === "sso" || authMode === "inherit") {
                    try {
                        const ssoRequest = {
                            scopes: scopesNeeded,
                            ...(loginHint && { loginHint }),
                            ...(sid && { sid }),
                        };

                        const response = await msalInstance.ssoSilent(ssoRequest);
                        msalInstance.setActiveAccount(response.account);
                        setAccount(response.account);
                        setIsAuthenticated(true);
                    } catch (ssoError) {
                        console.error("SSO Silent failed:", ssoError);

                        if (ssoError instanceof InteractionRequiredAuthError) {
                            setError("Authentication required. Please sign in.");
                        } else {
                            setError("SSO authentication failed.");
                        }
                    }
                }
            } catch (error) {
                console.error("Auth initialization error:", error);
                setError("Failed to initialize authentication.");
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();

        // Listen for authentication messages from parent
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === "cts-copilot-auth") {
                const { loginHint, sid } = event.data;

                // The copilot already runs inside an iframe created by copilot.js.
                // ssoSilent opens a *third* nested iframe which enterprise BSSO
                // extensions refresh before MSAL can read the token hash → timeout.
                // Use loginPopup(prompt=none) instead: a real popup window that AAD
                // resolves silently using existing session cookies then auto-closes,
                // without ever touching the hidden-iframe mechanism BSSO disrupts.
                const alreadyInIframe = window !== window.parent;
                if (alreadyInIframe) {
                    if (loginHint) setMessageLoginHint(loginHint);
                    // Guard: if MSAL hasn't finished initializing yet, wait for it.
                    // loginPopup on an uninitialized instance throws
                    // BrowserAuthError: uninitialized_public_client_application.
                    if (!msalInitialized.current) {
                        await msalInstance.initialize();
                        msalInitialized.current = true;
                    }
                    setIsLoading(true);
                    try {
                        const response = await msalInstance.loginPopup({
                            scopes: scopesNeeded,
                            ...(loginHint && { loginHint }),
                            prompt: "none", // silent — auto-closes if session exists
                        });
                        msalInstance.setActiveAccount(response.account);
                        setAccount(response.account);
                        setIsAuthenticated(true);
                        setError(null);
                        // Notify parent so copilot.js can call iframe.focus() from
                        // outside — belt-and-suspenders on top of window.focus() below.
                        window.parent.postMessage(
                            {
                                type: "cts-copilot-auth-success",
                                account: {
                                    username: response.account.username,
                                    name: response.account.name,
                                },
                            },
                            "*",
                        );
                    } catch {
                        // No active session or popup was blocked by the browser.
                        // loginHint is already stored; user will see the Sign In button.
                    } finally {
                        setIsLoading(false);
                        // When the popup closes the browser returns focus to the
                        // parent page (the host that embeds copilot.js), not back to
                        // this iframe. Re-focus this window so keyboard events reach
                        // the chat input without the user needing an extra click.
                        window.focus();
                    }
                    return;
                }

                try {
                    const response = await msalInstance.ssoSilent({
                        scopes: scopesNeeded,
                        loginHint,
                        sid,
                    });
                    msalInstance.setActiveAccount(response.account);
                    setAccount(response.account);
                    setIsAuthenticated(true);
                    setError(null);
                } catch (error) {
                    console.error("Parent auth sync failed:", error);
                    if (loginHint) setMessageLoginHint(loginHint);
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [authMode, loginHint, sid]);

    const handleLogin = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Prefer hint from postMessage (messageLoginHint) over the URL-param
            // prop (loginHint) — the parent page is the authoritative source for
            // which account to use when embedding the copilot.
            const effectiveHint = messageLoginHint ?? loginHint;
            const loginRequest = {
                scopes: scopesNeeded,
                ...(effectiveHint && { loginHint: effectiveHint }),
                prompt: "select_account",
            };

            const response = await msalInstance.loginPopup(loginRequest);
            msalInstance.setActiveAccount(response.account);
            setAccount(response.account);
            setIsAuthenticated(true);

            // Notify parent of successful login
            window.parent.postMessage(
                {
                    type: "cts-copilot-auth-success",
                    account: {
                        username: response.account.username,
                        name: response.account.name,
                    },
                },
                "*",
            );
        } catch (error) {
            console.error("Login error:", error);

            if (error instanceof BrowserAuthError) {
                if (error.errorCode === "popup_window_error") {
                    setError("Popups are blocked. Please allow popups for this site.");
                } else {
                    setError(`Login failed: ${error.message}`);
                }
            } else {
                setError("Login failed. Please try again.");
            }
        } finally {
            setIsLoading(false);
            // Restore keyboard focus to this iframe after the popup closes.
            window.focus();
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <Center h="100vh" style={{ background: theme.colors.gray[0] }}>
                <Flex direction="column" align="center" gap="md">
                    <div
                        style={{
                            width: "40px",
                            height: "40px",
                            border: "3px solid #e5e7eb",
                            borderTopColor: theme.colors.purple[6],
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                        }}
                    />
                    <Text c="dimmed" size="sm">
                        Initializing...
                    </Text>
                </Flex>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </Center>
        );
    }

    // Not authenticated - show login UI
    if (!isAuthenticated) {
        return (
            <MsalProvider instance={msalInstance}>
                <Center h="100vh" style={{ background: theme.colors.gray[0], padding: "20px" }}>
                    <Flex
                        direction="column"
                        align="center"
                        gap="md"
                        style={{
                            maxWidth: "300px",
                            width: "100%",
                            padding: "24px",
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Text fw={600} size="lg" c={theme.colors.purple[8]}>
                            Knowledge Search
                        </Text>
                        <Text c="dimmed" size="sm" ta="center">
                            Sign in to access technical documentation
                        </Text>

                        {error && (
                            <Text c="red" size="sm" ta="center">
                                {error}
                            </Text>
                        )}

                        <Button
                            fullWidth
                            onClick={handleLogin}
                            loading={isLoading}
                            variant="gradient"
                            gradient={{ from: "purple.8", to: "purple.6" }}
                        >
                            Sign in with Microsoft
                        </Button>

                        <Text c="dimmed" size="xs" ta="center">
                            This will open a popup window for authentication
                        </Text>
                    </Flex>
                </Center>
            </MsalProvider>
        );
    }

    // Authenticated - render children directly; CopilotLayout manages its own height.
    // Do NOT wrap in overflow:hidden — it clips the textarea at the bottom of the
    // chat layout and makes the input unresponsive to pointer events.
    return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
